import React, { useMemo, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { XMarkIcon } from '@heroicons/react/24/outline';
import {
  ACCOUNT_SETTINGS,
  FieldDef,
  SettingDef,
  isFeatureEnabled,
} from '../config/accountSettings';

interface Account {
  id: number;
  slug: string;
  name: string;
  settings: Record<string, unknown>;
  user_count: number;
}

interface Props {
  account: Account;
  onClose: () => void;
  onSaved: () => void;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const authHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const asString = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));

/** Initial form state derived from the registry + the saved settings blob. */
function buildInitialState(account: Account): {
  values: Record<string, string>;
  enabled: Record<string, boolean>;
} {
  const values: Record<string, string> = {};
  const enabled: Record<string, boolean> = {};
  for (const def of ACCOUNT_SETTINGS) {
    enabled[def.key] = isFeatureEnabled(account, def.key);
    for (const field of def.fields) {
      values[field.key] = asString(account.settings?.[field.key]);
    }
  }
  return { values, enabled };
}

const AccountSettingsModal: React.FC<Props> = ({ account, onClose, onSaved }) => {
  const initial = useMemo(() => buildInitialState(account), [account]);
  const [values, setValues] = useState(initial.values);
  const [enabled, setEnabled] = useState(initial.enabled);
  const [isSaving, setIsSaving] = useState(false);

  const setValue = (key: string, v: string) => setValues((s) => ({ ...s, [key]: v }));
  const setEnable = (key: string, v: boolean) => setEnabled((s) => ({ ...s, [key]: v }));

  // Group settings by category for section headers.
  const sections = useMemo(() => {
    const map = new Map<string, SettingDef[]>();
    for (const def of ACCOUNT_SETTINGS) {
      const list = map.get(def.category) || [];
      list.push(def);
      map.set(def.category, list);
    }
    return Array.from(map.entries());
  }, []);

  const handleSave = async () => {
    // Validate required fields on enabled settings.
    for (const def of ACCOUNT_SETTINGS) {
      if (def.togglable && !enabled[def.key]) continue;
      for (const field of def.fields) {
        const v = (values[field.key] || '').trim();
        if (field.maxLength && v.length > field.maxLength) {
          toast.error(`${field.label} is too long (max ${field.maxLength} chars)`);
          return;
        }
      }
    }

    // Build the merged settings JSON. Preserve unknown keys from the existing
    // blob so we don't accidentally delete settings the registry hasn't
    // grown a UI for yet.
    const merged: Record<string, unknown> = { ...(account.settings || {}) };
    const features: Record<string, boolean> = {
      ...((merged.features as Record<string, boolean>) || {}),
    };

    for (const def of ACCOUNT_SETTINGS) {
      const isOn = !def.togglable || enabled[def.key];
      if (def.togglable) features[def.key] = enabled[def.key];

      if (!isOn) continue;

      // Gather raw input values for this setting's fields, normalize, then
      // pass through any derive() to compute extras (e.g. GL regex from example).
      const raw: Record<string, string> = {};
      for (const field of def.fields) {
        let v = (values[field.key] || '').trim();
        if (field.uppercase) v = v.toUpperCase();
        raw[field.key] = v;
      }
      const final = def.derive ? def.derive(raw) : raw;

      Object.keys(final).forEach((k) => {
        const v = final[k];
        if (v) merged[k] = v;
        else delete merged[k];
      });
    }
    merged.features = features;

    setIsSaving(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/admin/accounts/${account.id}`,
        { settings: merged },
        { headers: authHeaders() },
      );
      toast.success(`Saved settings for ${account.name}`);
      onSaved();
      onClose();
    } catch (e) {
      // Surface a useful error to the user instead of swallowing it. Backends
      // can return error info under any of: detail (FastAPI standard),
      // message (older Pydantic), or just a plain string. Fall through to
      // status code + raw body if nothing structured comes back.
      const err = e as {
        response?: { status?: number; data?: unknown; statusText?: string };
        message?: string;
      };
      let msg = 'Unknown error — check browser console.';
      const data = err.response?.data;
      if (typeof data === 'string') {
        msg = data;
      } else if (data && typeof data === 'object') {
        const obj = data as Record<string, unknown>;
        if (typeof obj.detail === 'string') msg = obj.detail;
        else if (Array.isArray(obj.detail)) msg = JSON.stringify(obj.detail);
        else if (typeof obj.message === 'string') msg = obj.message;
        else msg = JSON.stringify(data);
      } else if (err.response?.status) {
        msg = `HTTP ${err.response.status} ${err.response.statusText || ''}`.trim();
      } else if (err.message) {
        msg = err.message;
      }
      // Log the full error so devs can inspect in DevTools too
      // eslint-disable-next-line no-console
      console.error('AccountSettingsModal save failed:', err.response || err);
      toast.error(`Save failed: ${msg}`, { duration: 8000 });
    } finally {
      setIsSaving(false);
    }
  };

  const renderField = (field: FieldDef) => (
    <div key={field.key}>
      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
        {field.label}
      </label>
      <input
        type="text"
        value={values[field.key] || ''}
        onChange={(e) => setValue(field.key, e.target.value)}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        className={`w-full px-3 py-2 text-sm border rounded-md dark:bg-gray-700 dark:text-white dark:border-gray-600 ${
          field.monospace ? 'font-mono' : ''
        } ${field.uppercase ? 'uppercase' : ''}`}
      />
      {field.helpText && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{field.helpText}</p>
      )}
    </div>
  );

  /** Special preview for GL format — shows the derived placeholder + error text. */
  const renderGlPreview = (def: SettingDef) => {
    if (def.key !== 'gl_format' || !def.derive) return null;
    const example = (values.gl_format_example || '').trim();
    if (!example) return null;
    const derived = def.derive({ gl_format_example: example });
    if (!derived.gl_format_placeholder) return null;
    return (
      <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-900/40 rounded text-xs space-y-1 border dark:border-gray-700">
        <div className="text-gray-500 dark:text-gray-400">Clerks will see:</div>
        <div className="font-mono text-gray-900 dark:text-gray-100">
          Placeholder: <span className="text-brand-600 dark:text-brand-400">{derived.gl_format_placeholder}</span>
        </div>
        <div className="font-mono text-gray-900 dark:text-gray-100">
          Error if invalid: <span className="text-red-500">{derived.gl_format_label}</span>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b dark:border-gray-700">
          <div>
            <h3 className="text-lg font-semibold dark:text-white">Account Settings</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{account.slug}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          {sections.map(([category, defs]) => (
            <section key={category}>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{category}</h4>
              <div className="space-y-4">
                {defs.map((def) => {
                  const isOn = !def.togglable || enabled[def.key];
                  return (
                    <div key={def.key}>
                      {def.togglable && (
                        <label className="flex items-center gap-2 mb-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!enabled[def.key]}
                            onChange={(e) => setEnable(def.key, e.target.checked)}
                            className="h-4 w-4 text-brand-600 border-gray-300 dark:border-gray-600 rounded focus:ring-brand-500"
                          />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {def.label}
                          </span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            (optional — leave off if this tenant doesn't use it)
                          </span>
                        </label>
                      )}
                      {def.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 ml-6">
                          {def.description}
                        </p>
                      )}
                      {isOn && (
                        <div className={`space-y-3 ${def.togglable ? 'ml-6' : ''}`}>
                          {def.fields.map(renderField)}
                          {renderGlPreview(def)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="flex justify-end gap-2 px-6 py-4 border-t dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 dark:text-white rounded-md"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-md disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountSettingsModal;
